class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.overlay = document.querySelector(".header__overlay");
		this.body = document.querySelector("body");
		this.headerTop = document.querySelector(".header__top");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.headerInput = document.querySelector(".header__search .search__input");

		// Tanuki — opzioni dal customizer, passate via data-attribute
		this.psConfig = {
			limit: parseInt(this.dataset.psLimit || "8", 10),
			types: this.dataset.psTypes || "product,article",
			highlight: this.dataset.psHighlight === "true",
			recent: this.dataset.psRecent === "true",
			suggestions: (this.dataset.psSuggestions || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean),
		};
		this.RECENT_KEY = "tnk_recent_searches";

		this.setupEventListeners();
	}

	/* ── Ricerche recenti (solo localStorage del visitatore) ───────────── */
	getRecentSearches() {
		if (!this.psConfig.recent) return [];
		try {
			const raw = localStorage.getItem(this.RECENT_KEY);
			return raw ? JSON.parse(raw).slice(0, 5) : [];
		} catch (e) {
			return [];
		}
	}

	saveRecentSearch(term) {
		if (!this.psConfig.recent || !term || term.length < 2) return;
		try {
			const list = this.getRecentSearches().filter(
				(t) => t.toLowerCase() !== term.toLowerCase(),
			);
			list.unshift(term);
			localStorage.setItem(this.RECENT_KEY, JSON.stringify(list.slice(0, 5)));
		} catch (e) {
			/* storage pieno o disabilitato: ignora */
		}
	}

	clearRecentSearches() {
		try {
			localStorage.removeItem(this.RECENT_KEY);
		} catch (e) {}
	}

	escapeHtml(str) {
		return String(str).replace(
			/[&<>"']/g,
			(c) =>
				({
					"&": "&amp;",
					"<": "&lt;",
					">": "&gt;",
					'"': "&quot;",
					"'": "&#39;",
				})[c],
		);
	}

	/* ── Pannello a campo vuoto: recenti + suggerimenti ────────────────── */
	renderIdlePanel() {
		const recent = this.getRecentSearches();
		const sugg = this.psConfig.suggestions;
		if (!recent.length && !sugg.length) return false;

		const searchUrl = (window.routes && routes.search_url) || "/search";
		const chip = (t) =>
			`<li class="predictive-search__chip-item" role="option" aria-selected="false">
				<a class="predictive-search__chip" tabindex="-1" href="${searchUrl}?q=${encodeURIComponent(t)}">${this.escapeHtml(t)}</a>
			</li>`;

		let html = '<div id="predictive-search-results" role="listbox" tabindex="-1"><div class="predictive-search__idle">';
		if (recent.length) {
			html += `<div class="predictive-search__idle-block">
					<p class="predictive-search__group-heading">
						Ricerche recenti
						<button type="button" class="predictive-search__clear" data-ps-clear-recent>Cancella</button>
					</p>
					<ul class="predictive-search__chips list-unstyled" role="group">${recent.map(chip).join("")}</ul>
				</div>`;
		}
		if (sugg.length) {
			html += `<div class="predictive-search__idle-block">
					<p class="predictive-search__group-heading">Ricerche suggerite</p>
					<ul class="predictive-search__chips list-unstyled" role="group">${sugg.map(chip).join("")}</ul>
				</div>`;
		}
		html += "</div></div>";

		this.predictiveSearchResults.innerHTML = html;
		this.predictiveSearchResults
			.querySelector("[data-ps-clear-recent]")
			?.addEventListener("mousedown", (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.clearRecentSearches();
				if (!this.renderIdlePanel()) this.close(false);
			});

		this.setAttribute("results", true);
		this.open();
		return true;
	}

	/* ── Evidenzia il termine cercato nei titoli ───────────────────────── */
	highlightTerm(term) {
		if (!this.psConfig.highlight || !term) return;
		const words = term
			.trim()
			.split(/\s+/)
			.filter((w) => w.length > 1)
			.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
		if (!words.length) return;
		const re = new RegExp(`(${words.join("|")})`, "gi");

		this.predictiveSearchResults
			.querySelectorAll("[data-ps-title]")
			.forEach((el) => {
				// textContent: non reintroduciamo mai markup dal titolo prodotto
				const text = el.textContent.trim();
				el.innerHTML = this.escapeHtml(text).replace(re, "<mark>$1</mark>");
			});
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
		this.addEventListener("keydown", this.onKeydown.bind(this));
		// Tanuki — memorizza la query anche quando si clicca un risultato
		this.addEventListener("click", (event) => {
			if (event.target.closest("a[href]")) {
				this.saveRecentSearch(this.getQuery());
			}
		});
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			// Tanuki — tornato vuoto: pannello recenti invece del dropdown chiuso
			this.closeResults(false);
			this.renderIdlePanel();
			return;
		}

		this.getSearchResults(this.searchTerm);
	}

	onFormSubmit(event) {
		this.saveRecentSearch(this.getQuery());
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a')
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			this.overlay.classList.add("visible");
			this.headerTop.classList.add("header__top--search");
			//this.body.classList.add("overflow-hidden");
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
		}

		if (!currentSearchTerm.length) {
			// Tanuki — campo vuoto: mostra ricerche recenti + popolari
			this.renderIdlePanel();
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			this.headerTop.classList.remove("header__top--search");
			this.overlay.classList.remove("visible");
			//this.body.classList.remove("overflow-hidden");
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		setTimeout(() => {
			if (!this.contains(document.activeElement)) this.close();
		});
	}

	onBlur() {
		this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			//case "ArrowUp":
			//	this.switchOption("up");
			//	break;
			//case "ArrowDown":
			//	this.switchOption("down");
			//	break;
			case "Enter":
				this.selectOption();
				break;
			case "Escape":
				this.closeResults(true);
				if (this.promoModal && this.classList.contains("search-modal__form")) {
					this.promoModal.classList.add("search__modal--hidden");
				}
				break;
		}
	}

	onKeydown(event) {
		// Prevent the cursor from moving in the input when using the up and down arrow keys
		if (event.code === "ArrowUp" || event.code === "ArrowDown") {
			event.preventDefault();
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
	}

	//switchOption(direction) {
	//	if (!this.getAttribute("open")) return;

	//	const moveUp = direction === "up";
	//	const selectedElement = this.querySelector('[aria-selected="true"]');

	//	// Filter out hidden elements (duplicated page and article resources) thanks
	//	// to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
	//	const allVisibleElements = Array.from(
	//		this.querySelectorAll("li, button.predictive-search__item"),
	//	).filter((element) => element.offsetParent !== null);
	//	let activeElementIndex = 0;

	//	if (moveUp && !selectedElement) return;

	//	let selectedElementIndex = -1;
	//	let i = 0;

	//	while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
	//		if (allVisibleElements[i] === selectedElement) {
	//			selectedElementIndex = i;
	//		}
	//		i++;
	//	}

	//	this.statusElement.textContent = "";

	//	if (!moveUp && selectedElement) {
	//		activeElementIndex =
	//			selectedElementIndex === allVisibleElements.length - 1
	//				? 0
	//				: selectedElementIndex + 1;
	//	} else if (moveUp) {
	//		activeElementIndex =
	//			selectedElementIndex === 0
	//				? allVisibleElements.length - 1
	//				: selectedElementIndex - 1;
	//	}

	//	if (activeElementIndex === selectedElementIndex) return;

	//	const activeElement = allVisibleElements[activeElementIndex];

	//	activeElement.setAttribute("aria-selected", true);
	//	if (selectedElement) selectedElement.setAttribute("aria-selected", false);

	//	this.input.setAttribute("aria-activedescendant", activeElement.id);
	//}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		// Tanuki — query estesa: più tipi di risorsa, ricerca anche su SKU/tag/
		// descrizione, limite configurabile (max 10, tetto dell'API Shopify).
		const params = new URLSearchParams();
		params.set("q", searchTerm);
		params.set("resources[type]", this.psConfig.types);
		params.set("resources[limit]", String(this.psConfig.limit));
		// senza limit_scope=each il limite vale sul TOTALE delle risorse
		// (prodotti + collezioni + articoli), non per singolo tipo
		params.set("resources[limit_scope]", "each");
		params.set("resources[options][unavailable_products]", "last");
		params.set(
			"resources[options][fields]",
			"title,product_type,variants.title,variants.sku,vendor,tag,body",
		);
		params.set("section_id", "predictive-search");

		fetch(`${routes.predictive_search_url}?${params.toString()}`, {
			signal: this.abortController.signal,
		})
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);
				this.renderSearchResults(resultsMarkup);
			})
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});
	}

	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.highlightTerm(this.searchTerm);
		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		const countEl = this.querySelector(
			"[data-predictive-search-live-region-count-value]",
		);
		if (countEl) this.setLiveRegionText(countEl.textContent);
	}

	//getResultsMaxHeight() {
	//	this.resultsMaxHeight =
	//		window.innerHeight -
	//		document.querySelector("#shopify-section-header").getBoundingClientRect()
	//			.bottom;
	//	return this.resultsMaxHeight;
	//}

	open() {
		//this.predictiveSearchResults.style.maxHeight =
		//	this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.remove("search__modal--hidden");
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		//this.resultsMaxHeight = false;
		this.predictiveSearchResults.removeAttribute("style");
		if (this.promoModal) {
			this.promoModal.classList.add("search__modal--hidden");
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
